'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface AIGapAnalysisProps {
  currentLanguage: string;
}

const AIGapAnalysis = ({ currentLanguage }: AIGapAnalysisProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const analysisResults = [
    {
      category: 'Critical Gaps',
      severity: 'high',
      icon: 'ExclamationTriangleIcon',
      items: [
        {
          title: 'Scope 3 Emissions Data Collection',
          description: 'Value chain emission data incomplete for 45% of suppliers',
          recommendation: 'Implement supplier engagement program with automated data collection tools',
          priority: 'Critical',
        },
        {
          title: 'Water Conservation Metrics',
          description: 'Missing water usage data for 12 manufacturing facilities',
          recommendation: 'Install IoT water meters and establish baseline metrics',
          priority: 'High',
        },
      ],
    },
    {
      category: 'Moderate Gaps',
      severity: 'medium',
      icon: 'InformationCircleIcon',
      items: [
        {
          title: 'Biodiversity Impact Assessment',
          description: 'Assessments completed for only 60% of operational sites',
          recommendation: 'Conduct comprehensive biodiversity surveys for remaining locations',
          priority: 'Medium',
        },
        {
          title: 'Social Metric Standardization',
          description: 'Inconsistent reporting formats across regional offices',
          recommendation: 'Implement unified data collection framework with training',
          priority: 'Medium',
        },
      ],
    },
    {
      category: 'Minor Gaps',
      severity: 'low',
      icon: 'CheckCircleIcon',
      items: [
        {
          title: 'ESG Policy Documentation',
          description: 'Some policies require minor updates to align with latest CSRD standards',
          recommendation: 'Schedule policy review and update cycle',
          priority: 'Low',
        },
      ],
    },
  ];

  const aiInsights = [
    {
      title: 'Pattern Detection',
      description: 'AI identified recurring data quality issues in Q3 Scope 2 emissions reporting',
      actionable: true,
    },
    {
      title: 'Benchmark Comparison',
      description: 'Your performance is 15% above industry average in renewable energy adoption',
      actionable: false,
    },
    {
      title: 'Predictive Alert',
      description: 'Projected to miss 2025 water reduction target by 8% at current rate',
      actionable: true,
    },
    {
      title: 'Optimization Opportunity',
      description: 'Consolidating 3 separate ESG dashboards could reduce reporting time by 40%',
      actionable: true,
    },
  ];

  const handleRunAnalysis = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
    }, 2000);
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="SparklesIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">AI-Powered Analysis</span>
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Intelligent Gap Analysis & Recommendations
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Leverage AI to identify compliance gaps, predict risks, and receive actionable recommendations
          </p>
        </div>

        {/* Analysis Trigger */}
        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-8 mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                Run Comprehensive Analysis
              </h3>
              <p className="text-sm text-text-secondary">
                Last analysis: 3 days ago • Next scheduled: Tomorrow
              </p>
            </div>
            <button
              onClick={handleRunAnalysis}
              disabled={isAnalyzing}
              className="px-8 py-4 bg-primary text-primary-foreground font-heading font-semibold rounded-lg hover:bg-primary/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center space-x-2">
                <Icon
                  name={isAnalyzing ? 'ArrowPathIcon' : 'PlayIcon'}
                  size={20}
                  className={isAnalyzing ? 'animate-spin' : ''}
                />
                <span>{isAnalyzing ? 'Analyzing...' : 'Start Analysis'}</span>
              </div>
            </button>
          </div>

          {isAnalyzing && (
            <div className="mt-6">
              <div className="w-full bg-surface rounded-full h-2 overflow-hidden">
                <div className="bg-primary h-full rounded-full animate-pulse" style={{ width: '100%' }} />
              </div>
            </div>
          )}
        </div>

        {/* Gap Analysis Results */}
        <div className="space-y-8 mb-12">
          {analysisResults.map((category, catIndex) => (
            <div key={catIndex} className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Icon
                  name={category.icon}
                  size={28}
                  className={`${
                    category.severity === 'high' ?'text-error'
                      : category.severity === 'medium' ?'text-warning' :'text-success'
                  }`}
                />
                <h3 className="font-heading font-bold text-xl text-text-primary">{category.category}</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    category.severity === 'high' ?'bg-error/10 text-error'
                      : category.severity === 'medium' ?'bg-warning/10 text-warning' :'bg-success/10 text-success'
                  }`}
                >
                  {category.items.length} {category.items.length === 1 ? 'Issue' : 'Issues'}
                </span>
              </div>

              <div className="space-y-4">
                {category.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="bg-surface border border-border rounded-lg p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-semibold text-text-primary flex-grow">{item.title}</h4>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          item.priority === 'Critical' ?'bg-error/10 text-error'
                            : item.priority === 'High' ?'bg-warning/10 text-warning'
                            : item.priority === 'Medium' ?'bg-info/10 text-info' :'bg-success/10 text-success'
                        }`}
                      >
                        {item.priority}
                      </span>
                    </div>

                    <p className="text-sm text-text-secondary mb-4">{item.description}</p>

                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="LightBulbIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-primary mb-1">AI Recommendation</p>
                          <p className="text-sm text-text-primary">{item.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* AI Insights */}
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <Icon name="CpuChipIcon" size={28} className="text-primary" />
            <h3 className="font-heading font-bold text-xl text-text-primary">Real-Time AI Insights</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {aiInsights.map((insight, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-lg p-5 hover:shadow-natural transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-text-primary">{insight.title}</h4>
                  {insight.actionable && (
                    <Icon name="BoltIcon" size={18} className="text-warning flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-text-secondary mb-4">{insight.description}</p>
                {insight.actionable && (
                  <button className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
                    Take Action →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIGapAnalysis;